import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { Search, Book, Calculator, Shapes, Ruler, Triangle, PieChart, Brain, ChevronRight, Timer, Award, TrendingUp, Lightbulb, Target, Sparkles, Infinity, Percent, DollarSign, Clock, Droplets, Car, Bot as Boat, FunctionSquare as Function, Equal, Box, Circle, Hexagon, Table, BarChart2, PieChart as PieChart2, LineChart, AirVent as Venn, ListOrdered, Dice1 as Dice, Users, Hash } from 'lucide-react';
import { categories } from './data';
import Footer from '../../components/Footer';

export function AptitudeHomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [popularTopics, setPopularTopics] = useState([]);

  // Calculate total number of topics for SEO description
  const totalTopics = categories.reduce((sum, category) => sum + category.topics.length, 0);

  // Set up popular topics on component mount
  useEffect(() => {
    // These would ideally be determined by analytics data
    const popular = [
      { name: 'Percentage', category: 'Arithmetic', url: '/exam/average-and-percentage-problems' },
      { name: 'Profit and Loss', category: 'Arithmetic', url: '/exam/profit-and-loss-questions' },
      { name: 'Time and Work', category: 'Arithmetic', url: '/exam/time-and-work-questions' },
      { name: 'Quadratic Equations', category: 'Algebra', url: '/exam/quadratic-equations-questions' },
      { name: 'Probability', category: 'Miscellaneous', url: '/exam/probability-questions' }
    ];
    setPopularTopics(popular);
  }, []);

  const filteredCategories = categories.map(category => ({
    ...category,
    topics: category.topics.filter(topic =>
      topic.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => 
    selectedCategory ? category.name === selectedCategory : true
  );

  const getCategoryColor = (name) => {
    switch (name) {
      case 'Arithmetic':
        return { 
          bg: 'bg-blue-50', 
          icon: 'text-blue-500', 
          hover: 'hover:bg-blue-100',
          button: 'bg-blue-500 hover:bg-blue-600'
        };
      case 'Algebra':
        return { 
          bg: 'bg-emerald-50', 
          icon: 'text-emerald-500', 
          hover: 'hover:bg-emerald-100',
          button: 'bg-emerald-500 hover:bg-emerald-600'
        };
      case 'Geometry':
        return { 
          bg: 'bg-cyan-50', 
          icon: 'text-cyan-500', 
          hover: 'hover:bg-cyan-100',
          button: 'bg-cyan-500 hover:bg-cyan-600'
        };
      case 'Mensuration':
        return { 
          bg: 'bg-amber-50', 
          icon: 'text-amber-500', 
          hover: 'hover:bg-amber-100',
          button: 'bg-amber-500 hover:bg-amber-600'
        };
      case 'Trigonometry':
        return { 
          bg: 'bg-rose-50', 
          icon: 'text-rose-500', 
          hover: 'hover:bg-rose-100',
          button: 'bg-rose-500 hover:bg-rose-600'
        };
      case 'Data Interpretation':
        return { 
          bg: 'bg-violet-50', 
          icon: 'text-violet-500', 
          hover: 'hover:bg-violet-100',
          button: 'bg-violet-500 hover:bg-violet-600'
        };
      default:
        return { 
          bg: 'bg-teal-50', 
          icon: 'text-teal-500', 
          hover: 'hover:bg-teal-100',
          button: 'bg-teal-500 hover:bg-teal-600'
        };
    }
  };

  const getTopicIcon = (name, category) => {
    const { icon } = getCategoryColor(category);
    const iconClass = `w-8 h-8 ${icon}`;
    switch (name.toLowerCase()) {
      // Arithmetic
      case 'number system': return <Hash className={iconClass} />;
      case 'simplification': return <Equal className={iconClass} />;
      case 'hcf and lcm': return <Infinity className={iconClass} />;
      case 'average': return <TrendingUp className={iconClass} />;
      case 'percentage': return <Percent className={iconClass} />;
      case 'profit and loss': return <DollarSign className={iconClass} />;
      case 'discount': return <DollarSign className={iconClass} />;
      case 'simple interest': return <TrendingUp className={iconClass} />;
      case 'compound interest': return <TrendingUp className={iconClass} />;
      case 'ratio and proportion': return <Equal className={iconClass} />;
      case 'partnership': return <Users className={iconClass} />;
      case 'mixture and alligation': return <Droplets className={iconClass} />;
      case 'time and work': return <Clock className={iconClass} />;
      case 'pipe and cistern': return <Droplets className={iconClass} />;
      case 'speed, time, and distance': return <Car className={iconClass} />;
      case 'boat and stream': return <Boat className={iconClass} />;
      
      // Algebra
      case 'basics of algebra': return <Function className={iconClass} />;
      case 'linear equations': return <Function className={iconClass} />;
      case 'quadratic equations': return <Function className={iconClass} />;
      
      // Geometry
      case 'basics of geometry (lines, angles)': return <Ruler className={iconClass} />;
      case 'triangles (properties, similarity, congruence)': return <Triangle className={iconClass} />;
      case 'circles (properties and theorems)': return <Circle className={iconClass} />;
      case 'polygons': return <Hexagon className={iconClass} />;
      case 'coordinate geometry': return <Target className={iconClass} />;
      
      // Data Interpretation
      case 'tabulation': return <Table className={iconClass} />;
      case 'bar graph': return <BarChart2 className={iconClass} />;
      case 'pie chart': return <PieChart2 className={iconClass} />;
      case 'line graph': return <LineChart className={iconClass} />;
      
      // Miscellaneous
      case 'sets and venn diagrams': return <Venn className={iconClass} />;
      case 'sequence and series': return <ListOrdered className={iconClass} />;
      case 'probability': return <Dice className={iconClass} />;
      case 'permutations and combinations': return <Sparkles className={iconClass} />;
      case 'problems on ages': return <Users className={iconClass} />;
      
      default: return <Lightbulb className={iconClass} />;
    }
  };

  const getCategoryIcon = (name) => {
    const { icon } = getCategoryColor(name);
    const iconClass = `w-10 h-10 ${icon}`;
    switch (name) {
      case 'Arithmetic': return <Calculator className={iconClass} />;
      case 'Algebra': return <Book className={iconClass} />;
      case 'Geometry': return <Shapes className={iconClass} />;
      case 'Mensuration': return <Ruler className={iconClass} />;
      case 'Trigonometry': return <Triangle className={iconClass} />;
      case 'Data Interpretation': return <PieChart className={iconClass} />;
      default: return <Brain className={iconClass} />;
    }
  };

  // Get total number of tests
  const getTotalTests = () => {
    return categories.reduce((sum, category) => 
      sum + category.topics.length * 200, 0); // Assuming 8 tests per topic
  };

  return (
    <>
      <Helmet>
        <title>Quantitative Aptitude Questions | Free Online Aptitude Test | Aptitude Mock Test</title>
        <meta name="description" content={`Practice 1000+ quantitative aptitude questions with our free online aptitude test platform. Take aptitude mock tests for bank, SSC, railway exams with detailed solutions.`} />
        <meta name="keywords" content="quantitative aptitude questions, online aptitude test, aptitude mock test, aptitude questions with answers, free aptitude test, aptitude practice test, bank exam aptitude, SSC aptitude, percentage problems, profit and loss, time and work" />
        <link rel="canonical" href="https://mocktestpro.com/quantitative-aptitude-questions" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Quantitative Aptitude Questions | Free Online Aptitude Test" />
        <meta property="og:description" content={`Practice 1000+ quantitative aptitude questions with our free online aptitude test platform. Take aptitude mock tests for competitive exams.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mocktestpro.com/quantitative-aptitude-questions" />
        <meta property="og:image" content="https://mocktestpro.com/aptitude-tests-banner.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Quantitative Aptitude Questions | Online Aptitude Test" />
        <meta name="twitter:description" content={`Practice quantitative aptitude questions with our free online aptitude test platform. Take aptitude mock tests for competitive exams.`} />
        <meta name="twitter:image" content="https://mocktestpro.com/aptitude-tests-banner.jpg" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="MockTestPro" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Hero Section */}
        <section className="py-12 px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Quantitative Aptitude Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Practice with our free online aptitude test platform featuring {getTotalTests()}+ questions for competitive exams
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href="#categories" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md">
              Take Aptitude Mock Test
            </a>
            <a href="#popular" className="px-6 py-3 bg-white text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition shadow-sm">
              Popular Aptitude Questions
            </a>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center text-blue-600">
                <Award className="w-5 h-5 mr-2" />
                <span className="font-medium">Why Take Our Online Aptitude Tests?</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="flex items-start">
                <div className="bg-blue-50 p-2 rounded-full mr-2">
                  <Timer className="w-4 h-4 text-blue-600" />
                </div>
                <span>Timed aptitude mock tests simulate exam conditions</span>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-50 p-2 rounded-full mr-2">
                  <Lightbulb className="w-4 h-4 text-blue-600" />
                </div>
                <span>Detailed solutions for all quantitative aptitude questions</span>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-50 p-2 rounded-full mr-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <span>Track your progress across different aptitude topics</span>
              </div>
            </div>
          </div>
        </section>

        {/* Online Test Platform Section */}
        <section className="my-12 bg-blue-50 p-8 rounded-xl">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-6">Free Online Aptitude Test Platform</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-4">🧮</div>
                <h3 className="text-lg font-semibold mb-2">1000+ Quantitative Aptitude Questions</h3>
                <p className="text-gray-600">
                  Practice with a comprehensive collection of aptitude questions covering all important topics for competitive exams.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-4">⏱️</div>
                <h3 className="text-lg font-semibold mb-2">Realistic Aptitude Mock Tests</h3>
                <p className="text-gray-600">
                  Take timed aptitude mock tests that simulate the actual exam environment to improve your speed and accuracy.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">Performance Analytics</h3>
                <p className="text-gray-600">
                  Get detailed performance reports after each online aptitude test to identify your strengths and weaknesses.
                </p>
              </div>
            </div>
          </div>
        </section>

         {/* Main Content */}
         <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Popular Topics Section */}
          <section id="popular" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Popular Quantitative Aptitude Questions</h2>
            <p className="text-gray-600 mb-6">
              These topics frequently appear in competitive exams. Take a free online aptitude test on any of these topics:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularTopics.map((topic) => {
                const category = categories.find(c => c.name === topic.category);
                const { bg, button } = getCategoryColor(topic.category);
                
                return (
                  <div
                    key={topic.name}
                    className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 ${bg} rounded-xl transition-colors duration-200`}>
                          {getTopicIcon(topic.name, topic.category)}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Timer className="w-5 h-5" />
                          <span>30 mins</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">{topic.name}</h3>
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <Award className="w-5 h-5 mr-2" />
                        <span>8 Aptitude Mock Tests Available</span>
                      </div>
                      <a
                        href={topic.url}
                        className={`w-full ${button} text-white py-3 px-4 rounded-lg shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center group no-underline`}
                      >
                        <span>Take Online Aptitude Test</span>
                        <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Search and Filter Section */}
          <section className="mb-8 space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Find Quantitative Aptitude Questions</h2>
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input
                type="text"
                placeholder="Search for aptitude questions (e.g., percentage, profit and loss, probability...)"
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search quantitative aptitude questions"
              />
            </div>
            
            {/* Category Tabs */}
            <div id="categories" className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200
                  ${!selectedCategory 
                    ? 'bg-blue-500 text-white shadow-sm' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`}
              >
                All Topics
              </button>
              {categories.map((category) => {
                const { bg, hover } = getCategoryColor(category.name);
                return (
                  <button
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200
                      ${selectedCategory === category.name 
                        ? `${bg} shadow-sm border border-gray-200` 
                        : `bg-white text-gray-700 ${hover} border border-gray-200`}`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Categories Grid */}
          <div className="space-y-12">
            {filteredCategories.map((category) => {
              const { bg, icon, button } = getCategoryColor(category.name);
              
              // Skip empty categories
              if (category.topics.length === 0) return null;
              
              return (
                <div key={category.name} className="space-y-6">
                  <div className={`flex items-center space-x-4 ${bg} p-6 rounded-xl shadow-sm border border-gray-100`}>
                    {getCategoryIcon(category.name)}
                    <div>
                      <h2 className={`text-2xl font-bold ${icon}`}>{category.name}</h2>
                      <p className="text-gray-600 mt-1">
                        {getCategoryDescription(category.name)} • {category.topics.length} Topics
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.topics.map((topic) => {
                      const { bg, hover } = getCategoryColor(category.name);
                      
                      return (
                        <div
                          key={topic.name}
                          className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className={`p-3 ${bg} rounded-xl ${hover} transition-colors duration-200`}>
                                {getTopicIcon(topic.name, category.name)}
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <Timer className="w-5 h-5" />
                                <span>30 mins</span>
                              </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">{topic.name}</h3>
                            <div className="flex items-center text-sm text-gray-500 mb-4">
                              <Award className="w-5 h-5 mr-2" />
                              <span>8 Aptitude Mock Tests Available</span>
                            </div>
                            <a
                              href={topic.url}
                              className={`w-full ${button} text-white py-3 px-4 rounded-lg shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center group no-underline`}
                            >
                              <span>Take Aptitude Mock Test</span>
                              <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Exam Preparation Section */}
          <section className="my-16 bg-white p-8 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Prepare for Competitive Exams with Quantitative Aptitude</h2>
            <p className="text-gray-600 mb-8">
              Our online aptitude tests and quantitative aptitude questions are designed to help you prepare for various competitive exams including:
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { name: 'Bank Exams', exams: 'SBI PO, IBPS PO, RBI, NABARD', icon: '🏦' },
                { name: 'SSC Exams', exams: 'SSC CGL, SSC CHSL, SSC MTS', icon: '🏛️' },
                { name: 'Railway Exams', exams: 'RRB NTPC, RRB Group D, RRB JE', icon: '🚆' },
                { name: 'Other Govt Exams', exams: 'UPSC, State PSC, Defense', icon: '📝' }
              ].map((exam) => (
                <div key={exam.name} className="bg-gray-50 p-6 rounded-lg border border-gray-100">
                  <div className="text-3xl mb-3">{exam.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{exam.name}</h3>
                  <p className="text-sm text-gray-600">{exam.exams}</p>
                </div>
              ))}
            </div>
          </section>
          
          {/* FAQ Section */}
          <section className="my-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">What types of quantitative aptitude questions are covered?</h3>
                <p className="text-gray-600">
                  Our platform covers all major types of quantitative aptitude questions including arithmetic (percentage, profit and loss, 
                  time and work), algebra, geometry, and data interpretation. These questions are designed to match the pattern and 
                  difficulty level of competitive exams.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">How do online aptitude tests help in exam preparation?</h3>
                <p className="text-gray-600">
                  Online aptitude tests simulate the actual exam environment, helping you improve your speed, accuracy, and time management. 
                  Regular practice with our aptitude mock tests will help you identify your weak areas and track your improvement over time.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Are these aptitude mock tests similar to actual competitive exams?</h3>
                <p className="text-gray-600">
                  Yes, our aptitude mock tests are designed based on the latest exam patterns of various competitive exams including 
                  bank exams, SSC, railways, and other government recruitment tests. The difficulty level and types of questions match 
                  the actual exams.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">How can I improve my score in quantitative aptitude sections?</h3>
                <p className="text-gray-600">
                  Regular practice with our online aptitude tests is key to improvement. Start with understanding basic concepts, 
                  learn shortcuts and tricks, and then take timed aptitude mock tests to improve your speed and accuracy. Review 
                  your mistakes and focus on weak areas.
                </p>
              </div>
            </div>
          </section>
          
          {/* Call to Action */}
          <section className="my-16 bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-8 rounded-xl shadow-md">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Practice Quantitative Aptitude Questions?</h2>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Start with our free online aptitude tests and improve your problem-solving skills today.
              </p>
              <a 
                href="#categories" 
                className="inline-block px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold shadow-sm hover:shadow-md transition"
              >
                Take Free Aptitude Mock Test
              </a>
            </div>
          </section>
        </main>
        <Footer />
      </div>
      
      {/* Structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Quantitative Aptitude Questions | Free Online Aptitude Test | Aptitude Mock Test",
          "description": `Practice ${totalTopics}+ quantitative aptitude questions with our free online aptitude test platform. Take aptitude mock tests for competitive exams.`,
          "url": "https://mocktestpro.com/quantitative-aptitude-questions",
          "provider": {
            "@type": "Organization",
            "name": "MockTestPro",
            "sameAs": "https://mocktestpro.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://mocktestpro.com/logo.png"
            }
          },
          "offers": {
            "@type": "Offer",
            "category": "Free",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "http://schema.org/InStock"
          },
          "courseWorkload": "PT60M",
          "hasCourseInstance": categories.flatMap(category => 
            category.topics.map(topic => ({
              "@type": "CourseInstance",
              "name": `${topic.name} Aptitude Questions`,
              "courseMode": "online",
              "courseSchedule": {
                "@type": "Schedule",
                "duration": "PT60M",
                "repeatFrequency": "daily",
                "repeatCount": "365"
              },
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "availability": "http://schema.org/InStock"
              }
            }))
          ),
          "keywords": [
            "quantitative aptitude questions",
            "online aptitude test",
            "aptitude mock test",
            "free aptitude test",
            "aptitude questions with solutions",
            "bank exam aptitude",
            "SSC aptitude questions",
            "competitive exam preparation",
            "aptitude practice test",
            "percentage problems",
            "profit and loss questions",
            "time and work problems"
          ]
        })}
      </script>

        </>
      );
    }
    
    // Helper function to get category descriptions
    function getCategoryDescription(categoryName) {
      switch(categoryName) {
        case 'Arithmetic':
          return 'Basic mathematical calculations for quantitative aptitude';
        case 'Algebra':
          return 'Equations and variables for aptitude tests';
        case 'Geometry':
          return 'Properties of shapes for quantitative questions';
        case 'Mensuration':
          return 'Measurement of geometric figures for aptitude';
        case 'Trigonometry':
          return 'Trigonometric concepts for competitive exams';
        case 'Data Interpretation':
          return 'Analysis of data for aptitude mock tests';
        case 'Miscellaneous':
          return 'Additional important aptitude topics';
        default:
          return 'Practice questions for online aptitude tests';
      }
    }
